import React from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  CheckBox,
  ErrorSpan,
  FormCaption,
  FormSubmit,
  InformSpan
} from '../../common/components';
import { Input } from '../../common/components/form/input.component';
import {
  AUTH_PATTERN,
  AUTH_VALIDATION_CONFIG,
  EmailErrors,
  PasswordErrors,
  TermsErrors
} from './validation.pattern';
import { APP_KEYS } from '../../common/consts';
import { ROUTER_LINKS } from '../../common/consts/app-keys.const';
import { ISignin } from '../types';
import { authService } from '../service/auth.service';
import { useThrottle } from '../../hooks/useThrottle';
import useAuth from '../feature/auth';

type Inputs = {
  email: string;
  password: string;
  terms: any;
};

export const Signup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setIsAuthenticated, checkAuth } = useAuth();
  const [isTermsAccepted, setIsTermsAccepted] = React.useState<boolean | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isValid }
  } = useForm<Inputs>({ mode: 'onChange' });

  React.useEffect(() => {
    if (watch('terms') === true) {
      setIsTermsAccepted(true);
    }
  }, [watch('terms')]);

  const signup = useMutation({
    mutationKey: [APP_KEYS.QUERY_KEYS.USER],
    mutationFn: async (data: ISignin) => authService.signup(data),
    onSuccess: async (result) => {
      if (result?.autoActivated) {
        setIsAuthenticated(true);
        await checkAuth();
        navigate(ROUTER_LINKS.DASHBOARD);
      } else {
        navigate(APP_KEYS.ROUTER_LINKS.ACTIVATE_ACCOUNT, {
          state: { email: watch('email') }
        });
      }
    },
    onError: (err: any) => {
      const message = err.response?.data?.message ?? '';
      if (['Credentials taken', 'User already exists'].includes(message)) {
        setError('email', {
          type: 'manual',
          message: t('auth.accountExists')
        });
      } else {
        setError('root', {
          type: 'manual',
          message:
            message || t('auth.networkError')
        });
      }
    }
  });
  const throttledSignup = useThrottle(signup.mutate);

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    if (data.terms !== true) {
      setIsTermsAccepted(false);
      return;
    }
    throttledSignup(data);
  };

  return (
    <>
      <FormCaption>{t('auth.signupTitle')}</FormCaption>
      <InformSpan>{t('auth.signupSubtitle')}</InformSpan>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
        <Input
          errors={errors}
          name="email"
          label={t('auth.email')}
          placeholder={t('auth.enterHere')}
          type="email"
          register={register.bind(null, 'email', {
            required: { message: EmailErrors.required, value: true },
            pattern: {
              value: AUTH_PATTERN.email,
              message: EmailErrors.pattern
            }
          })}
        />
        <Input
          errors={errors}
          name="password"
          label={t('auth.password')}
          placeholder={t('auth.enterHere')}
          type="password"
          register={register.bind(null, 'password', {
            required: { message: PasswordErrors.required, value: true },
            pattern: {
              value: AUTH_PATTERN.password,
              message: PasswordErrors.pattern
            },
            minLength: {
              value: AUTH_VALIDATION_CONFIG.password.minLength,
              message: PasswordErrors.minLength
            },
            maxLength: {
              value: AUTH_VALIDATION_CONFIG.password.maxLength,
              message: PasswordErrors.maxLength
            }
          })}
        />

        <div className="flex justify-between items-center max-w-[392px] my-[22px]">
          <CheckBox
            description={
              <span>
                {t('auth.agreeToTerms')}{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline font-medium">
                  {t('auth.termsOfService')}
                </a>{' '}
                {t('auth.and')}{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline font-medium">
                  {t('auth.privacyPolicy')}
                </a>
              </span>
            }
            register={register.bind(null, 'terms')}
            terms
          />
        </div>
        {isTermsAccepted === false && <ErrorSpan>{TermsErrors.required}</ErrorSpan>}

        {errors.root && (
          <p className="text-red-500 font-body text-[14px] mb-4">
            {errors.root.message}
          </p>
        )}

        <FormSubmit
          className="mt-[2px]"
          isLoading={signup.isPending}
          isDisabled={!isValid}
          value={t('auth.signupButton')}
        />

        <div className="text-center font-body text-[16px]">
          <span className="text-gray-900">{t('auth.alreadyHaveAccount')} </span>
          <Link
            to={ROUTER_LINKS.SIGNIN}
            className="text-base-black font-medium hover:opacity-60"
          >
            {t('auth.loginButton')}
          </Link>
        </div>
      </form>
    </>
  );
};
